import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { SoldBy } from "@/components/SoldBy";

const MASK = (url) => ({
    maskImage: `url(${url})`, WebkitMaskImage: `url(${url})`, maskSize: "contain", WebkitMaskSize: "contain",
    maskPosition: "center", WebkitMaskPosition: "center", maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat",
});

const PAPER = "#f3f4f6";

/**
 * The one product card every shelf uses — signs, garments, the DTF counter. The photo is the card;
 * the description rises out of the bottom edge on hover.
 * `contain` sits a cut-out garment on solid light grey with dark type — no gradient over the shirt.
 */
export function PhotoCard({ to, image, contain, tintHex, name, detail, price, priceNote, turnaround, colors = [], kind, store, theme,
    mine, tag, mixed, receded = "", cta = "Design & order", testid, imageTestid }) {
    const url = mediaUrl(image);
    const ink = contain ? "#18181b" : "#fff";
    const sub = contain ? "#52525b" : "#e4e4e7";
    return (
        <Link to={to} data-testid={testid} style={{ "--c": theme.primary, background: contain ? PAPER : undefined }}
            className={`group relative block aspect-[4/3] overflow-hidden rounded-2xl border shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-[color:var(--c)] hover:shadow-[0_24px_40px_-24px_rgba(15,23,42,0.5)] ${contain ? "" : "bg-zinc-100"} ${mine ? "border-[color:var(--c)]" : "border-zinc-200"} ${receded}`}>
            {image && (
                <img src={url} alt={name} loading="lazy" data-testid={imageTestid}
                    className={`absolute inset-0 h-full w-full transition-transform duration-700 ease-out group-hover:scale-105 ${contain ? "object-contain p-3 pb-14" : "object-cover group-hover:scale-110"}`} />
            )}
            {tintHex && image && (
                <span className="pointer-events-none absolute inset-0 p-3 pb-14 transition-transform duration-700 ease-out group-hover:scale-105" style={{ mixBlendMode: "multiply" }}>
                    <span className="block h-full w-full" style={{ backgroundColor: tintHex, ...MASK(url) }} />
                </span>
            )}
            {!contain && <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />}

            {(tag || kind) && (
                <span className="absolute left-3 top-3 rounded-full px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest shadow-sm"
                    style={tag ? { background: theme.primary, color: theme.on } : { background: "rgba(255,255,255,0.92)", color: "#3f3f46" }} data-testid={testid ? `${testid}-kind` : undefined}>
                    {tag ? "This shop" : kind}
                </span>
            )}
            {turnaround && (
                <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-700 shadow-sm"
                    data-testid={testid ? `${testid}-turnaround` : undefined}>
                    {turnaround}
                </span>
            )}

            <div className={`absolute inset-x-0 bottom-0 p-4 ${contain ? "bg-white/85 backdrop-blur-sm" : ""}`} style={{ color: ink }}>
                <h3 className="font-display text-lg font-bold leading-tight" style={{ color: ink }}>{name}</h3>
                <div className="max-h-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:mt-1.5 group-hover:max-h-24 group-hover:opacity-100">
                    {detail && <p className="text-xs leading-snug" style={{ color: sub }}>{detail}</p>}
                    {mixed && <SoldBy store={store} className="mt-1.5" withFulfillment={false} />}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold transition-transform duration-300 group-hover:translate-x-1"
                        style={contain ? { background: theme.primary, color: theme.on } : { background: "#fff", color: theme.ink || theme.primary }}>
                        {cta} <ArrowRight size={12} />
                    </span>
                    {price != null && (
                        <span className="font-mono text-[11px] font-bold" style={{ color: ink }} data-testid={testid ? `${testid}-price` : undefined}>
                            from ${Number(price || 0).toFixed(price % 1 ? 2 : 0)}{priceNote ? <span className="font-normal opacity-70"> {priceNote}</span> : null}
                        </span>
                    )}
                    {colors.length > 1 && (
                        <span className="ml-auto flex items-center gap-1">
                            {colors.map((c) => <span key={c.name} title={c.name} className={`h-2.5 w-2.5 rounded-full border ${contain ? "border-black/20" : "border-white/50"}`} style={{ background: c.hex || "#fff" }} />)}
                        </span>
                    )}
                </div>
            </div>
        </Link>
    );
}

/** Garment document → card props, shared by every apparel shelf. */
export const garmentCard = (p, kind, activeStore, activeSlug, cardTheme) => {
    const colors = p.colors?.length ? p.colors : [{ name: p.color_name, hex: p.shirt_color_hex }];
    const front = (p.mockups?.front || {}).url;
    const tintable = p.tintable && (p.colors?.[0]?.hex || "").toLowerCase() !== "#ffffff";
    return {
        to: `/shirts/${p.category}/${p.slug}`,
        image: p.photo_override || front || kind?.image_url,
        contain: !p.photo_override,
        tintHex: tintable && !p.photo_override ? p.colors?.[0]?.hex : null,
        colors,
        name: p.name,
        kind: kind?.name,
        detail: [p.brand, p.colors?.length > 1 ? `${p.colors.length} colors` : p.color_name, p.description || p.short_description].filter(Boolean).join(" · "),
        price: (p.pricing?.base_per_piece || 0) + (p.pricing?.print_locations?.front || 0),
        priceNote: "each",
        turnaround: p.turnaround,
        store: p.store,
        theme: cardTheme(p, activeStore),
        mine: Boolean(activeSlug && p.store?.slug === activeSlug),
        testid: `shirt-product-${p.slug}`,
        imageTestid: `shirt-card-image-${p.slug}`,
    };
};
